/**
 * TickerCard — Full desktop trading card for symbol search results.
 * Tabs: Overview · Chart · Options · Greeks · News · AI Analysis
 * Data: Public.com live quotes + option chain + Greeks, Yahoo OHLCV,
 *       IV history, TEA score, news, Zi regime analysis.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Line, Bar } from "react-chartjs-2";
import { createChart, ColorType, CrosshairMode, CandlestickSeries, HistogramSeries, createSeriesMarkers } from "lightweight-charts";
import { getCached, hydrate, getInflight } from "../lib/symbolCache";
import QuickTradeModal from "./QuickTradeModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Same real bare-ticker set backend/public_api.py routes crypto quotes by —
// duplicated client-side (like QuickTradeModal's own OCC parsing) so this
// file can pick EQUITY vs CRYPTO for an order without a network round trip.
// MKR excluded — Public.com's live API no longer supports quoting it (backend removed it from the same list for the same reason).
const CRYPTO_TICKERS = new Set(["BTC","ETH","DOGE","SOL","ADA","LTC","BCH","LINK","AVAX","DOT","SHIB","UNI","XLM","AAVE","MATIC","ALGO","ATOM","ETC","XRP","NEAR","GRT","CRV","COMP","BAT","FIL","MANA","SAND","APE","USDC","USDT"]);
const fmt  = (v, d = 2) => v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtC = (v) => v == null ? "—" : `$${fmt(Math.abs(v), 2)}`;
const fmtB = (v) => v == null ? "—" : Number(v) >= 1e9 ? `$${fmt(v / 1e9, 2)}B` : Number(v) >= 1e6 ? `$${fmt(v / 1e6, 1)}M` : `$${fmt(v, 0)}`;
const pct  = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${fmt(v, 2)}%`;

const CHART_OPTS = (color = "#60a5fa") => ({
  responsive: true, maintainAspectRatio: false, animation: false,
  plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(15,23,42,0.95)", borderColor: color + "44", borderWidth: 1, titleColor: "#94a3b8", bodyColor: "#e2e8f0", padding: 10 } },
  scales: {
    x: { ticks: { color: "#64748b", font: { size: 9 }, maxTicksLimit: 8 }, grid: { color: "var(--grid)" } },
    y: { ticks: { color: "#64748b", font: { size: 9 } }, grid: { color: "var(--grid)" }, position: "right" },
  },
});

const TABS = [
  { id: "overview", label: "Overview",  icon: "◎" },
  { id: "chart",    label: "Chart",     icon: "↗" },
  { id: "options",  label: "Options",   icon: "⬡" },
  { id: "greeks",   label: "Greeks",    icon: "Δ" },
  { id: "news",     label: "News",      icon: "◈" },
  { id: "analysis", label: "AI Analysis", icon: "✦" },
];

// ─── Stat tile ───────────────────────────────────────────────────────────────
function Stat({ label, value, sub, color, large }) {
  return (
    <div style={{ padding: "12px 14px", borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: large ? 22 : 16, fontWeight: 700, color: color || "var(--text)", lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--dim)" }}>{sub}</div>}
    </div>
  );
}

// ─── TEA badge ───────────────────────────────────────────────────────────────
function TeaBadge({ score, sentiment }) {
  // A null score means the catalyst wasn't graded. Every threshold below is
  // false for null, so without this it would render as the bearish red band.
  const graded = score != null;
  const color = !graded ? "#64748b" : score >= 70 ? "#4ade80" : score >= 45 ? "#fbbf24" : "#f87171";
  const bg    = !graded ? "rgba(100,116,139,0.1)" : score >= 70 ? "rgba(74,222,128,0.1)" : score >= 45 ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, background: bg, border: `1px solid ${color}44` }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      <span style={{ fontSize: 11, fontWeight: 700, color }}>{graded ? `TEA ${score}` : "TEA —"}</span>
      <span style={{ fontSize: 10, color: "var(--muted)" }}>{graded ? sentiment : "not graded"}</span>
    </div>
  );
}

// ─── Mini sparkline for hero ──────────────────────────────────────────────────
function MiniSparkline({ bars, color }) {
  if (!bars?.length) return null;
  const closes = bars.map(b => b.c);
  const labels = bars.map(() => "");
  const data = { labels, datasets: [{ data: closes, borderColor: color, backgroundColor: color + "18", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 }] };
  const opts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
  };
  return <div style={{ height: 48, width: 120 }}><Line data={data} options={opts} /></div>;
}

// ─── Option contract card ─────────────────────────────────────────────────────
// Real Zi class colors — same as AutopilotPanel.js's ziClassColor, not a new
// palette invented for this card (see plan Finding 2).
const ZI_CLASS_COLOR = { ULTRA_GEM: "#4ade80", GEM: "#60a5fa", NEUTRAL: "#facc15", AVOID: "#f87171" };

function ContractCard({ c, type, underlying, onSelect, scoreInfo }) {
  const itm   = type === "call" ? c.strike <= underlying : c.strike >= underlying;
  const atm   = Math.abs(c.strike - underlying) < 0.5;
  const mid   = c.bid != null && c.ask != null ? (c.bid + c.ask) / 2 : c.last;
  const color = type === "call" ? "#4ade80" : "#f87171";
  const ziColor = scoreInfo?.ziClass ? ZI_CLASS_COLOR[scoreInfo.ziClass] : null;
  return (
    <div onClick={() => onSelect?.({ ...c, type })} style={{
      padding: "10px 12px", borderRadius: 12, cursor: "pointer",
      background: atm ? "rgba(167,139,250,0.1)" : itm ? (type === "call" ? "rgba(74,222,128,0.05)" : "rgba(248,113,113,0.05)") : "var(--layer2)",
      border: scoreInfo?.diamond ? "1.5px solid #67e8f9" : atm ? "1.5px solid #a78bfa" : `1px solid ${itm ? color + "33" : "var(--border)"}`,
      boxShadow: scoreInfo?.diamond ? "0 0 12px rgba(103,232,249,0.25)" : "none",
      position: "relative", transition: "transform 0.1s, box-shadow 0.1s",
    }}>
      {atm && <div style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", fontSize: 8, fontWeight: 800, color: "#a78bfa", background: "var(--bg)", padding: "0 5px", letterSpacing: 0.5 }}>ATM</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5, gap: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: atm ? "#a78bfa" : "var(--text)" }}>${fmt(c.strike, 0)}</span>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {scoreInfo?.diamond && <span title="Ultra Gem / Gem — real Zi chain score" style={{ fontSize: 8, fontWeight: 800, color: "#67e8f9", padding: "1px 5px", borderRadius: 4, background: "rgba(103,232,249,0.14)" }}>◆ DIAMOND</span>}
          {ziColor && !scoreInfo?.diamond && <span style={{ fontSize: 8, fontWeight: 800, color: ziColor, padding: "1px 5px", borderRadius: 4, background: ziColor + "22" }}>{scoreInfo.ziClass.replace("_", " ")}</span>}
          {itm && <span style={{ fontSize: 9, fontWeight: 700, color, padding: "1px 5px", borderRadius: 4, background: color + "22" }}>ITM</span>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>Mid <span style={{ color: "var(--text)", fontWeight: 600 }}>{mid != null ? fmtC(mid) : "—"}</span></div>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>OI <span style={{ color: "var(--text)", fontWeight: 600 }}>{(c.oi || 0).toLocaleString()}</span></div>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>Bid <span style={{ color: "#4ade80", fontWeight: 600 }}>{fmtC(c.bid)}</span></div>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>Ask <span style={{ color: "#f87171", fontWeight: 600 }}>{fmtC(c.ask)}</span></div>
        <div style={{ fontSize: 10, color: "var(--muted)" }}>Vol <span style={{ color: "var(--text)", fontWeight: 600 }}>{(c.volume || 0).toLocaleString()}</span></div>
      </div>
    </div>
  );
}

// ─── Swipe-to-confirm — the UI gesture only. Real order placement happens
// in QuickTradeModal (dry-run + risk-check + zbucks-paper support already
// built there); this just decides "the user means it" before that modal
// opens, same deliberate-action pattern a swipe gesture is for anywhere. ──
function TradeSwipeConfirm({ mode, label, onConfirm, onCancel }) {
  const trackRef  = useRef(null);
  const handleRef = useRef(null);
  const fillRef   = useRef(null);
  const draggingRef = useRef(false);
  const PAD = 3, HANDLE_W = 38, CONFIRM_PCT = 0.82;
  const color = mode === "sell" ? "#f87171" : "#4ade80";

  const travel = (w) => Math.max(1, w - HANDLE_W - PAD * 2);
  const posFor = (clientX, width) => {
    const rect = trackRef.current.getBoundingClientRect();
    const raw = clientX - rect.left - HANDLE_W / 2;
    return Math.max(PAD, Math.min(PAD + travel(width), raw));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    handleRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = posFor(e.clientX, rect.width);
    handleRef.current.style.left = x + "px";
    const pct = (x - PAD) / travel(rect.width);
    fillRef.current.style.width = Math.min(100, pct * 100 + 8) + "%";
  };
  const onPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const rect = trackRef.current.getBoundingClientRect();
    const x = posFor(e.clientX, rect.width);
    const pct = (x - PAD) / travel(rect.width);
    if (pct >= CONFIRM_PCT) {
      handleRef.current.style.left = (PAD + travel(rect.width)) + "px";
      fillRef.current.style.width = "100%";
      onConfirm();
    } else {
      handleRef.current.style.left = PAD + "px";
      fillRef.current.style.width = "0%";
    }
  };

  return (
    <div ref={trackRef} style={{ position: "relative", height: 44, borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)", overflow: "hidden" }}>
      <div ref={fillRef} style={{ position: "absolute", inset: 0, width: "0%", background: color, opacity: 0.2, pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "var(--muted)", pointerEvents: "none" }}>{label}</div>
      <button onClick={onCancel} style={{ position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", fontSize: 10, fontWeight: 700, color: "var(--dim)", background: "none", border: "none", cursor: "pointer", zIndex: 3 }}>Cancel</button>
      <div ref={handleRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        tabIndex={0} role="slider" aria-label={`Slide to confirm ${mode}`} aria-valuemin={0} aria-valuemax={100}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onConfirm(); } if (e.key === "Escape") onCancel(); }}
        style={{ position: "absolute", top: 3, left: 3, width: 38, height: 38, borderRadius: 10, background: color, display: "flex", alignItems: "center", justifyContent: "center", cursor: "grab", touchAction: "none", zIndex: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0f" strokeWidth="2.6"><path d="m9 18 6-6-6-6"/></svg>
      </div>
    </div>
  );
}

// ─── Trade the underlying — stock or crypto, net-new (TickerCard had zero
// trade actions before this). Buy/Sell only, no qty/swipe shown until
// tapped — same progressive-disclosure pattern used throughout. ──────────
function TradeCard({ symbol, price, onTrade }) {
  const [mode, setMode] = useState(null); // null | "buy" | "sell"
  const [qty, setQty]   = useState(1);

  return (
    <div className="card">
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Trade {symbol}</div>
      {!mode ? (
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-buy" style={{ flex: 1 }} onClick={() => setMode("buy")}>Buy</button>
          <button className="btn-sell" style={{ flex: 1 }} onClick={() => setMode("sell")}>Sell</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 2, background: "var(--layer1)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 32, height: 34, background: "none", border: "none", color: "var(--text)", fontSize: 15, cursor: "pointer" }}>−</button>
              <span className="num" style={{ minWidth: 30, textAlign: "center", fontWeight: 700, fontSize: 13 }}>{qty}</span>
              <button onClick={() => setQty(q => Math.min(9999, q + 1))} style={{ width: 32, height: 34, background: "none", border: "none", color: "var(--text)", fontSize: 15, cursor: "pointer" }}>+</button>
            </div>
            {price != null && <div style={{ fontSize: 11, color: "var(--muted)" }}>≈ ${(qty * price).toLocaleString(undefined, { maximumFractionDigits: 2 })} · $0 commission</div>}
          </div>
          <TradeSwipeConfirm mode={mode} label={`Slide to ${mode} ${qty} · ${symbol}`}
            onCancel={() => setMode(null)}
            onConfirm={() => { onTrade(mode, qty); setMode(null); }} />
        </div>
      )}
    </div>
  );
}

// ─── Candlestick chart (TradingView lightweight-charts) ──────────────────────
function CandleChart({ bars, events = [], range = "1M", height = 340 }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const candleRef    = useRef(null);
  const volRef       = useRef(null);

  // Build candle + volume data from raw OHLCV bars
  const days  = range === "1W" ? 7 : range === "1M" ? 30 : range === "3M" ? 90 : bars.length;
  const slice = bars.slice(-days);

  const candleData = slice.map(b => ({
    time:  new Date(b.t).toISOString().slice(0, 10),
    open:  b.o ?? b.c,
    high:  b.h ?? b.c,
    low:   b.l ?? b.c,
    close: b.c,
  }));

  const volData = slice.map(b => ({
    time:  new Date(b.t).toISOString().slice(0, 10),
    value: b.v || 0,
    color: (b.c >= (b.o ?? b.c)) ? "rgba(74,222,128,0.5)" : "rgba(248,113,113,0.5)",
  }));

  // Event marker data
  const markers = events
    .filter(ev => candleData.some(c => c.time === ev.date))
    .map(ev => ({
      time:     ev.date,
      position: ev.change >= 0 ? "aboveBar" : "belowBar",
      color:    ev.color,
      shape:    ev.type === "earnings" ? "square" : ev.change >= 0 ? "arrowUp" : "arrowDown",
      text:     ev.icon + " " + ev.label,
    }));

  useEffect(() => {
    if (!containerRef.current || !candleData.length) return;

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor:  "#64748b",
        fontSize:   11,
      },
      grid: {
        vertLines:  { color: "rgba(255,255,255,0.04)" },
        horzLines:  { color: "rgba(255,255,255,0.04)" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)", scaleMarginBottom: 0.25 },
      timeScale: {
        borderColor:      "rgba(255,255,255,0.08)",
        timeVisible:      true,
        secondsVisible:   false,
        fixLeftEdge:      true,
        fixRightEdge:     true,
      },
      handleScroll: false,
      handleScale:  false,
    });

    // Candle series (v5 API)
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor:          "#4ade80",
      downColor:        "#f87171",
      borderUpColor:    "#4ade80",
      borderDownColor:  "#f87171",
      wickUpColor:      "#4ade80",
      wickDownColor:    "#f87171",
      priceScaleId:     "right",
    });
    candleSeries.setData(candleData);
    if (markers.length) createSeriesMarkers(candleSeries, markers);

    // Volume histogram on separate scale (v5 API)
    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat:  { type: "volume" },
      priceScaleId: "vol",
    });
    chart.priceScale("vol").applyOptions({ scaleMarginTop: 0.8, scaleMarginBottom: 0 });
    volSeries.setData(volData);

    chartRef.current = chart;
    candleRef.current = candleSeries;
    volRef.current = volSeries;

    // Resize observer — only fires when width actually changes, breaking the feedback loop
    let rafId = null;
    let lastW = containerRef.current.clientWidth;
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!containerRef.current || !chartRef.current) return;
        const w = containerRef.current.clientWidth;
        if (w !== lastW) {
          lastW = w;
          chartRef.current.applyOptions({ width: w });
        }
      });
    });
    ro.observe(containerRef.current);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bars, range, events.length]);

  if (!bars?.length) return (
    <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 13 }}>No OHLCV data</div>
  );

  return <div ref={containerRef} style={{ width: "100%", height, overflow: "hidden" }} />;
}

// ─── Real Black-Scholes (same formula/r=5% as TradingWorkstation.js's
// bsOptionValue and the backend's engine.black_scholes) — used to drive the
// What-If Simulator's live recompute without a network round-trip per drag
// tick. Not an approximation invented for this feature; it's the same closed
// -form pricing formula, just evaluated client-side. ─────────────────────────
function erf(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const s = x < 0 ? -1 : 1; x = Math.abs(x);
  const t = 1 / (1 + p * x);
  return s * (1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x));
}
function bsFull(S, K, T, iv, type) {
  if (T <= 0 || !iv) {
    const intrinsic = Math.max(0, type === "call" ? S - K : K - S);
    return { value: intrinsic, delta: type === "call" ? (S > K ? 1 : 0) : (S < K ? -1 : 0), pop: type === "call" ? (S > K ? 100 : 0) : (S < K ? 100 : 0) };
  }
  const d1 = (Math.log(S / K) + (0.05 + iv * iv / 2) * T) / (iv * Math.sqrt(T));
  const d2 = d1 - iv * Math.sqrt(T);
  const nd1 = 0.5 * (1 + erf(d1 / Math.sqrt(2)));
  const nd2 = 0.5 * (1 + erf(d2 / Math.sqrt(2)));
  const value = type === "call" ? S * nd1 - K * Math.exp(-0.05 * T) * nd2 : K * Math.exp(-0.05 * T) * (1 - nd2) - S * (1 - nd1);
  const delta = type === "call" ? nd1 : nd1 - 1;
  const pop = type === "call" ? (1 - nd2) * 100 : nd2 * 100;
  return { value, delta, pop };
}

// ─── Option detail modal ──────────────────────────────────────────────────────
function generateOptionInsight({ type, strike, premium, underlying, breakeven, breakevenPct, pop, dte, theta, iv, delta }) {
  const dir       = type === "call" ? "bullish" : "bearish";
  const bePct     = Math.abs(breakevenPct).toFixed(1);
  const ivLevel   = iv > 0.55 ? "elevated" : iv > 0.30 ? "moderate" : "low";
  const thetaAmt  = theta ? Math.abs(theta * 100).toFixed(2) : null;
  const popText   = pop != null ? `${pop.toFixed(0)}%` : "unknown";

  const lines = [
    `This ${type} is a ${dir} position targeting a move ${type === "call" ? "above" : "below"} $${breakeven.toFixed(2)}.`,
    pop != null ? `Delta-implied probability of profit: ${popText} — ${pop > 60 ? "favorable odds" : pop > 40 ? "moderate probability" : "speculative positioning"}.` : "",
    iv != null ? `IV at ${(iv * 100).toFixed(0)}% is ${ivLevel} — ${ivLevel === "elevated" ? "inflated premium, consider selling" : ivLevel === "moderate" ? "fairly priced" : "cheap, good for buying"}.` : "",
    `Breakeven requires a ${bePct}% ${type === "call" ? "rally" : "decline"} from current price${dte ? ` within ${dte} days` : ""}.`,
    thetaAmt ? `Time decay burns $${thetaAmt}/day — ${dte && dte < 14 ? "high urgency, gamma risk accelerating" : "manageable with time remaining"}.` : "",
    `Max loss is the $${(premium * 100).toFixed(0)} premium paid per contract. ${type === "call" ? "Max gain is theoretically unlimited." : `Max gain capped at $${((strike - premium) * 100).toFixed(0)}.`}`,
  ].filter(Boolean);

  return lines.join(" ");
}

function OptionDetailModal({ contract, underlying, greeks, expiry, symbol, onClose, onTrade }) {
  const [optQty, setOptQty]   = useState(1);
  const [optMode, setOptMode] = useState(null); // null | "buy" — buy-only, opening a new contract position
  const type     = contract.type || "call";
  const strike   = contract.strike;
  const bid      = contract.bid;
  const ask      = contract.ask;
  const mid      = bid != null && ask != null ? (bid + ask) / 2 : contract.last || 0;
  const premium  = mid || 0;
  const exp      = contract.expiry || expiry;
  const typeColor = type === "call" ? "#4ade80" : "#f87171";

  const g   = greeks.find(gr => gr.osi === contract.osi) || {};
  const dte = exp ? Math.max(0, Math.ceil((new Date(exp) - new Date()) / 86400000)) : null;

  // ── What-If Simulator state — price + days-from-now sliders. Default
  // (price=underlying, daysFromNow=dte) reproduces the exact original static
  // "at expiration" curve, so nothing changes visually until a slider moves.
  const [whatIfPrice, setWhatIfPrice] = useState(null);
  const [whatIfDays, setWhatIfDays]   = useState(null);
  const simPrice     = whatIfPrice ?? underlying;
  const daysFromNow  = whatIfDays ?? (dte ?? 0);
  const hasRealIv    = g.iv != null && dte != null;

  // Breakeven & PoP
  const breakeven    = type === "call" ? strike + premium : strike - premium;
  const breakevenPct = ((breakeven - underlying) / underlying) * 100;
  const pop          = g.delta != null ? Math.abs(g.delta) * 100 : null;

  // P&L curve across ±30% range — real Black-Scholes value (bsFull) when a
  // real IV is available, so the curve genuinely reflects time value at the
  // slider's chosen day rather than only the at-expiration payoff. Falls
  // back to the original intrinsic-only shape when IV isn't available.
  const range  = Math.max(underlying * 0.30, 20);
  const minP   = underlying - range;
  const maxP   = underlying + range;
  const steps  = 200;
  const prices = Array.from({ length: steps + 1 }, (_, i) => minP + ((maxP - minP) * i / steps));
  const simT   = hasRealIv ? Math.max(0, dte - daysFromNow) / 365 : 0;
  const pnls   = prices.map(s => {
    if (hasRealIv) return (bsFull(s, strike, simT, g.iv, type).value - premium) * 100;
    const intr = type === "call" ? Math.max(0, s - strike) : Math.max(0, strike - s);
    return (intr - premium) * 100;
  });

  const curIdx = prices.reduce((b, p, i) => Math.abs(p - underlying) < Math.abs(prices[b] - underlying) ? i : b, 0);
  const beIdx  = prices.reduce((b, p, i) => Math.abs(p - breakeven) < Math.abs(prices[b] - breakeven) ? i : b, 0);
  const simIdx = prices.reduce((b, p, i) => Math.abs(p - simPrice) < Math.abs(prices[b] - simPrice) ? i : b, 0);

  const maxLoss = -(premium * 100);
  const maxGain = type === "call" ? null : (strike - premium) * 100;

  const simResult = hasRealIv ? bsFull(simPrice, strike, simT, g.iv, type) : null;
  const simPnl    = simResult != null ? (simResult.value - premium) * 100 : null;
  const simPnlPct = simPnl != null && premium > 0 ? (simPnl / (premium * 100)) * 100 : null;
  const atExpiration = daysFromNow >= (dte ?? 0);

  // 4 scenarios
  const scenarios = [
    { label: "Strong Bear", move: -0.12, icon: "⬇⬇" },
    { label: "Bear",        move: type === "call" ? -0.05 : 0.05, icon: "⬇" },
    { label: "Bull",        move: type === "call" ? 0.08 : -0.08, icon: "⬆" },
    { label: "Strong Bull", move: 0.15, icon: "⬆⬆" },
  ].map(sc => {
    const target = underlying * (1 + sc.move);
    const intr   = type === "call" ? Math.max(0, target - strike) : Math.max(0, strike - target);
    const pnl    = (intr - premium) * 100;
    const pnlPct = premium > 0 ? (pnl / (premium * 100)) * 100 : 0;
    const isPos  = pnl >= 0;
    return { ...sc, target, pnl, pnlPct, isPos };
  });

  // AI insight
  const insight = generateOptionInsight({ type, strike, premium, underlying, breakeven, breakevenPct, pop, dte, theta: g.theta, iv: g.iv, delta: g.delta });

  // Chart — third marker (simIdx, accent purple) tracks the What-If price
  // slider; only drawn distinctly from curIdx once the slider has moved.
  const showSimMarker = simIdx !== curIdx;
  const ptRadii = prices.map((_, i) => i === curIdx ? 7 : i === beIdx ? 5 : (showSimMarker && i === simIdx) ? 7 : 0);
  const ptBg    = prices.map((_, i) => i === curIdx ? "#fff" : i === beIdx ? "#fbbf24" : (showSimMarker && i === simIdx) ? "#a78bfa" : "transparent");
  const ptBw    = prices.map((_, i) => (i === curIdx || i === beIdx || (showSimMarker && i === simIdx)) ? 2 : 0);

  const chartData = {
    labels: prices.map(p => `$${p.toFixed(0)}`),
    datasets: [{
      data: pnls,
      borderWidth: 2.5,
      tension: 0.2,
      pointRadius: ptRadii,
      pointBackgroundColor: ptBg,
      pointBorderColor: "#0f172a",
      pointBorderWidth: ptBw,
      fill: { target: { value: 0 }, above: "rgba(74,222,128,0.12)", below: "rgba(248,113,113,0.12)" },
      segment: {
        borderColor: ctx => {
          const y0 = ctx.p0.parsed.y, y1 = ctx.p1.parsed.y;
          if (y0 >= 0 && y1 >= 0) return "#4ade80";
          if (y0 <= 0 && y1 <= 0) return "#f87171";
          return "#94a3b8";
        },
      },
    }],
  };

  const chartOpts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(9,14,27,0.97)", borderColor: "rgba(255,255,255,0.08)", borderWidth: 1,
        titleColor: "#64748b", bodyColor: "#e2e8f0", padding: 12,
        callbacks: {
          title: items => `Underlying $${parseFloat(items[0].label.replace("$", "")).toFixed(2)}`,
          label: ctx => {
            const v = ctx.parsed.y;
            return ` P&L: ${v >= 0 ? "+" : ""}$${v.toFixed(0)} per contract`;
          },
          afterLabel: ctx => {
            const i = ctx.dataIndex;
            if (i === curIdx) return "  ← Current price";
            if (i === beIdx)  return "  ← Breakeven";
            return "";
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: "#475569", font: { size: 9 }, maxTicksLimit: 10 }, grid: { color: "rgba(255,255,255,0.03)" } },
      y: {
        position: "right",
        grid: { color: "rgba(255,255,255,0.03)" },
        ticks: { color: "#475569", font: { size: 9 }, callback: v => v >= 0 ? `+$${v}` : `-$${Math.abs(v)}` },
      },
    },
  };

  // Greeks bar helper
  const GreekBar = ({ label, value, min, max, color, note }) => {
    const pct = value != null && max !== min ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 0;
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: color || "var(--text)" }}>
            {value != null ? Number(value).toFixed(4) : "—"}
          </span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.06)" }}>
          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 2, background: color || "#60a5fa", transition: "width 0.4s ease" }} />
        </div>
        {note && <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 2 }}>{note}</div>}
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Backdrop */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(12px)" }} />

      {/* Card */}
      <div style={{ position: "relative", width: "100%", maxWidth: 820, maxHeight: "92vh", overflowY: "auto", borderRadius: 20, background: "linear-gradient(160deg, #090e1b 0%, #0f172a 100%)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 40px 100px rgba(0,0,0,0.7)", scrollbarWidth: "thin" }}>

        {/* ── Header ── */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>{symbol}</span>
              <span style={{ padding: "3px 10px", borderRadius: 8, background: typeColor + "22", border: `1px solid ${typeColor}44`, fontSize: 11, fontWeight: 800, color: typeColor, letterSpacing: 0.5 }}>
                {type.toUpperCase()}
              </span>
              <span style={{ padding: "3px 10px", borderRadius: 8, background: "rgba(255,255,255,0.06)", fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>
                ${strike} Strike
              </span>
              {exp && <span style={{ fontSize: 11, color: "var(--muted)" }}>Exp {exp}{dte != null ? ` · ${dte}d` : ""}</span>}
            </div>
            <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace" }}>{contract.osi || "—"}</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#64748b", width: 32, height: 32, borderRadius: 10, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
        </div>

        {/* ── Price strip ── */}
        <div style={{ padding: "14px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 24, flexWrap: "wrap" }}>
          {[
            { l: "Mid",      v: fmtC(mid),  accent: "#fff",     large: true },
            { l: "Bid",      v: fmtC(bid),  accent: "#4ade80" },
            { l: "Ask",      v: fmtC(ask),  accent: "#f87171" },
            { l: "Volume",   v: (contract.volume || 0).toLocaleString() },
            { l: "Open Int", v: (contract.oi || 0).toLocaleString() },
            { l: "IV",       v: g.iv != null ? `${(g.iv * 100).toFixed(1)}%` : "—", accent: "#fbbf24" },
          ].map(s => (
            <div key={s.l}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{s.l}</div>
              <div style={{ fontSize: s.large ? 20 : 14, fontWeight: s.large ? 900 : 700, color: s.accent || "var(--text)" }}>{s.v}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 22 }}>

          {/* ── P&L Chart ── */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{atExpiration ? "P&L at Expiration" : `P&L · ${daysFromNow === 0 ? "Today" : `+${daysFromNow}d from now`}`}</div>
              <div style={{ display: "flex", gap: 14, fontSize: 10, color: "#475569", flexWrap: "wrap" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", display: "inline-block" }} />Current ${fmt(underlying, 2)}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fbbf24", display: "inline-block" }} />BE ${breakeven.toFixed(2)}
                </span>
                {showSimMarker && (
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", display: "inline-block" }} />What-If ${simPrice.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
            <div style={{ height: 220, borderRadius: 12, background: "rgba(255,255,255,0.02)", padding: "12px 8px 8px", overflow: "hidden" }}>
              <Line data={chartData} options={chartOpts} />
            </div>
          </div>

          {/* ── What-If Simulator — real Black-Scholes recompute, no network
              round-trip per drag. Only interactive when a real IV came back
              from /market/greeks/batch; otherwise explains why, rather than
              faking a slider that can't actually move the number. ── */}
          <div style={{ padding: "16px 18px", borderRadius: 14, background: "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.18)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#a78bfa" }}>What-If Simulator</span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}>REAL BLACK-SCHOLES</span>
            </div>
            {!hasRealIv ? (
              <div style={{ fontSize: 11, color: "var(--muted)" }}>No live IV came back for this contract yet, so the simulator can't recompute a real price — showing the at-expiration curve only.</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 14 }}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>Stock Price</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#60a5fa" }}>${simPrice.toFixed(2)}</span>
                    </div>
                    <input type="range" min={minP} max={maxP} step={0.5} value={simPrice}
                      onChange={e => setWhatIfPrice(parseFloat(e.target.value))}
                      style={{ width: "100%", accentColor: "#60a5fa" }} />
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>Days From Now</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#a78bfa" }}>{daysFromNow}d</span>
                    </div>
                    <input type="range" min={0} max={dte} step={1} value={daysFromNow}
                      onChange={e => setWhatIfDays(parseInt(e.target.value, 10))}
                      style={{ width: "100%", accentColor: "#a78bfa" }} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
                  {[
                    { l: "Option Value", v: `$${simResult.value.toFixed(2)}`, color: "#60a5fa" },
                    { l: "P&L", v: `${simPnl >= 0 ? "+" : ""}$${simPnl.toFixed(0)}`, color: simPnl >= 0 ? "#4ade80" : "#f87171" },
                    { l: "Return", v: `${simPnlPct >= 0 ? "+" : ""}${simPnlPct.toFixed(1)}%`, color: simPnlPct >= 0 ? "#4ade80" : "#f87171" },
                    { l: "Delta", v: simResult.delta.toFixed(3), color: "#4ade80" },
                    { l: "PoP", v: `${simResult.pop.toFixed(0)}%`, color: "#fbbf24" },
                  ].map(m => (
                    <div key={m.l} style={{ padding: "8px 6px", borderRadius: 8, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <div style={{ fontSize: 8, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{m.l}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: m.color }}>{m.v}</div>
                    </div>
                  ))}
                </div>
                <button onClick={() => { setWhatIfPrice(null); setWhatIfDays(null); }}
                  style={{ marginTop: 10, fontSize: 10, color: "#64748b", background: "none", border: "none", textDecoration: "underline", cursor: "pointer", padding: 0 }}>
                  Reset · spot price, at expiration
                </button>
              </>
            )}
          </div>

          {/* ── Key metrics ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            {[
              { l: "Breakeven",  v: `$${breakeven.toFixed(2)}`, sub: `${breakevenPct > 0 ? "+" : ""}${breakevenPct.toFixed(1)}% from here`, color: "#fbbf24" },
              { l: "Max Loss",   v: `$${Math.abs(maxLoss).toFixed(0)}`,  sub: "per contract (100 shares)", color: "#f87171" },
              { l: "Max Gain",   v: maxGain != null ? `$${maxGain.toFixed(0)}` : "Unlimited", sub: type === "call" ? "theoretically unlimited" : `at $0 underlying`, color: "#4ade80" },
              { l: "Prob. Profit", v: pop != null ? `${pop.toFixed(0)}%` : "—", sub: pop != null ? "delta-implied" : "delta unavailable", color: pop > 50 ? "#4ade80" : pop > 35 ? "#fbbf24" : "#f87171" },
            ].map(m => (
              <div key={m.l} style={{ padding: "12px 14px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>{m.l}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: m.color || "var(--text)", lineHeight: 1.1 }}>{m.v}</div>
                <div style={{ fontSize: 9, color: "#475569", marginTop: 4 }}>{m.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Greeks + Scenarios side by side ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

            {/* Greeks */}
            <div style={{ padding: "16px 18px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fff", marginBottom: 14 }}>Greeks</div>
              <GreekBar label="Δ Delta"  value={g.delta} min={-1}    max={1}   color={g.delta > 0 ? "#4ade80" : "#f87171"} note="Price sensitivity per $1 move" />
              <GreekBar label="Γ Gamma"  value={g.gamma} min={0}     max={0.2} color="#60a5fa"  note="Delta change rate" />
              <GreekBar label="Θ Theta"  value={g.theta} min={-0.5}  max={0}   color="#f87171"  note={g.theta ? `$${Math.abs(g.theta * 100).toFixed(2)}/day time decay` : "Time decay"} />
              <GreekBar label="ν Vega"   value={g.vega}  min={0}     max={0.5} color="#a78bfa"  note="Sensitivity to 1% IV change" />
              {g.iv != null && (
                <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 8, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)" }}>
                  <div style={{ fontSize: 9, color: "#fbbf24", fontWeight: 700, marginBottom: 2 }}>IMPLIED VOLATILITY</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: "#fbbf24" }}>{(g.iv * 100).toFixed(1)}%</div>
                  <div style={{ fontSize: 9, color: "#64748b" }}>{g.iv > 0.5 ? "Elevated — seller's edge" : g.iv > 0.3 ? "Moderate — fairly priced" : "Low — cheap premium"}</div>
                </div>
              )}
            </div>

            {/* Scenarios */}
            <div style={{ padding: "16px 18px", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#fff", marginBottom: 14 }}>Price Scenarios at Expiry</div>
              {scenarios.map((sc, i) => (
                <div key={i} style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 10, background: sc.isPos ? "rgba(74,222,128,0.06)" : "rgba(248,113,113,0.06)", border: `1px solid ${sc.isPos ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: sc.isPos ? "#4ade80" : "#f87171" }}>{sc.icon} {sc.label}</div>
                      <div style={{ fontSize: 10, color: "#475569" }}>Target: ${sc.target.toFixed(2)} ({sc.move > 0 ? "+" : ""}{(sc.move * 100).toFixed(0)}%)</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 14, fontWeight: 900, color: sc.isPos ? "#4ade80" : "#f87171" }}>
                        {sc.pnl >= 0 ? "+" : ""}${sc.pnl.toFixed(0)}
                      </div>
                      <div style={{ fontSize: 9, color: "#475569" }}>{sc.pnlPct >= 0 ? "+" : ""}{sc.pnlPct.toFixed(0)}% ROI</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Time decay forecast ── */}
          {dte != null && g.theta != null && (
            <div style={{ padding: "14px 18px", borderRadius: 14, background: "rgba(248,113,113,0.05)", border: "1px solid rgba(248,113,113,0.15)" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#f87171", marginBottom: 10 }}>Θ Time Decay Forecast</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {[7, 14, 21, dte].filter((d, i, arr) => d <= dte && arr.indexOf(d) === i).slice(0, 4).map(d => {
                  const decay = Math.abs(g.theta * 100) * d;
                  const remaining = Math.max(0, (premium * 100) - decay);
                  return (
                    <div key={d} style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#64748b", marginBottom: 3 }}>In {d}d</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#f87171" }}>-${decay.toFixed(0)}</div>
                      <div style={{ fontSize: 9, color: "#475569" }}>${remaining.toFixed(0)} left</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Omega AI Insight ── */}
          <div style={{ padding: "16px 18px", borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.2)" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#a78bfa", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span>✦</span> Omega AI Analysis
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.8 }}>{insight}</div>
          </div>

          {/* ── Buy this contract — net-new, same swipe-confirm + real
              QuickTradeModal pattern as the underlying Trade card. ── */}
          {onTrade && (
            optMode === "buy" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 2, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }}>
                    <button onClick={() => setOptQty(q => Math.max(1, q - 1))} style={{ width: 32, height: 34, background: "none", border: "none", color: "#fff", fontSize: 15, cursor: "pointer" }}>−</button>
                    <span style={{ minWidth: 34, textAlign: "center", fontWeight: 700, fontSize: 13, color: "#fff" }}>{optQty}</span>
                    <button onClick={() => setOptQty(q => Math.min(50, q + 1))} style={{ width: 32, height: 34, background: "none", border: "none", color: "#fff", fontSize: 15, cursor: "pointer" }}>+</button>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>≈ ${(optQty * premium * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} · $0 commission</div>
                </div>
                <TradeSwipeConfirm mode="buy" label={`Slide to buy ${optQty} contract${optQty === 1 ? "" : "s"}`}
                  onCancel={() => setOptMode(null)}
                  onConfirm={() => { onTrade(contract, optQty); setOptMode(null); }} />
              </div>
            ) : (
              <button className="btn-buy" style={{ width: "100%" }} onClick={() => setOptMode("buy")}>
                Buy {type === "call" ? "Call" : "Put"} · ${strike}
              </button>
            )
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Event detection ─────────────────────────────────────────────────────────
function computeEvents(bars, earningsDates = []) {
  if (!bars?.length) return [];
  const avgVol = bars.reduce((s, b) => s + (b.v || 0), 0) / bars.length;
  const events = [];
  const tagged = new Set();

  earningsDates.forEach(date => {
    const d = date?.slice(0, 10);
    if (!d) return;
    const bar = bars.find(b => new Date(b.t).toISOString().slice(0, 10) === d);
    if (!bar) return;
    const idx = bars.indexOf(bar);
    const prev = bars[idx - 1];
    const change = prev ? ((bar.c - prev.c) / prev.c) * 100 : 0;
    const volRatio = avgVol ? (bar.v || 0) / avgVol : 1;
    tagged.add(d);
    events.push({
      type: "earnings", date: d, t: bar.t, price: bar.c, change, volRatio,
      icon: "⭐", label: "Earnings", color: "#a78bfa",
      insight: `Earnings report — stock ${change > 0 ? "surged +" : "dropped "}${Math.abs(change).toFixed(1)}% · Vol ${volRatio.toFixed(1)}× avg · ${Math.abs(change) > 8 ? "Major market reaction" : Math.abs(change) > 3 ? "Meaningful reaction" : "Muted reaction"}`,
    });
  });

  for (let i = 1; i < bars.length; i++) {
    const b = bars[i];
    const prev = bars[i - 1];
    const change = ((b.c - prev.c) / prev.c) * 100;
    const volRatio = avgVol ? (b.v || 0) / avgVol : 1;
    const d = new Date(b.t).toISOString().slice(0, 10);
    if (tagged.has(d)) continue;

    if (Math.abs(change) >= 4) {
      const isSurge = change > 0;
      tagged.add(d);
      events.push({
        type: isSurge ? "surge" : "drop", date: d, t: b.t, price: b.c, change, volRatio,
        icon: isSurge ? "🚀" : "⚠️",
        label: `${isSurge ? "+" : ""}${change.toFixed(1)}%`,
        color: isSurge ? "#4ade80" : "#f87171",
        insight: isSurge
          ? `Omega: Surged +${change.toFixed(1)}% · ${volRatio > 2 ? `${volRatio.toFixed(1)}× avg vol — institutional accumulation signal` : "Strong momentum — possible upgrade, news, or options flow"}`
          : `Omega: Dropped ${change.toFixed(1)}% · ${volRatio > 2 ? `${volRatio.toFixed(1)}× avg vol — heavy distribution` : "Sell-off on moderate vol — watch support levels"}`,
      });
    } else if (volRatio > 3.5 && Math.abs(change) >= 1) {
      tagged.add(d);
      events.push({
        type: "volume", date: d, t: b.t, price: b.c, change, volRatio,
        icon: "🔊",
        label: `${volRatio.toFixed(1)}× Vol`,
        color: "#fbbf24",
        insight: `Omega: ${volRatio.toFixed(1)}× avg volume with ${change > 0 ? "+" : ""}${change.toFixed(1)}% move — unusual institutional or options activity`,
      });
    }
  }

  return events.sort((a, b) => new Date(a.t) - new Date(b.t));
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function TickerCard({ symbol, onClose }) {
  // Start without spinner if cache is already warm
  const [tab, setTab]           = useState("overview");
  const [profile, setProfile]   = useState(null);
  const [quote, setQuote]       = useState(null);
  const [ohlcv, setOhlcv]       = useState(null);
  const [intraday, setIntraday] = useState(null);
  const [ivHist, setIvHist]     = useState(null);
  const [tea, setTea]           = useState(null);
  const [news, setNews]         = useState([]);
  const [regime, setRegime]     = useState(null);
  const [expirations, setExpirations] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [chain, setChain]       = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [greeks, setGreeks]     = useState([]);
  const [chainScore, setChainScore] = useState({}); // `${type}-${strike}` -> { ziClass, diamond, quickZi }
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [loading, setLoading]   = useState(() => !getCached(symbol));
  const [chartRange, setChartRange] = useState("1M");
  const [chartMode, setChartMode]   = useState("candle"); // "candle" | "price" | "intraday" | "iv"
  const [selectedContract, setSelectedContract] = useState(null);
  const [quickTrade, setQuickTrade] = useState(null); // { position, mode } for the real QuickTradeModal
  const [events, setEvents]           = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [teaDetail, setTeaDetail]     = useState(null);
  const [deepRead, setDeepRead]       = useState(null);
  const [deepReadLoading, setDeepReadLoading] = useState(false);
  const [deepReadError, setDeepReadError]     = useState(null);
  const [analysisError, setAnalysisError]     = useState(null);
  const quoteTimerRef = useRef(null);
  const deepReadCalledRef = useRef(false);
  const analysisCalledRef = useRef(false);

  // ── Initial load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!symbol) return;
    setProfile(null); setQuote(null); setOhlcv(null); setIntraday(null); setIvHist(null); setTea(null);
    setNews([]); setRegime(null); setChain(null); setGreeks([]);
    setEvents([]); setSelectedEvent(null);
    setDeepRead(null); setTeaDetail(null); setDeepReadLoading(false);
    setDeepReadError(null); setAnalysisError(null); setAnalysis(null);
    deepReadCalledRef.current = false; analysisCalledRef.current = false;

    let cancelled = false; // stale-fetch guard

    const applyData = (bundle, ohlcvData, intradayData, ivData, regimeData, expData, earningsData) => {
      // Always clear the spinner — even on a stale-fetch from a previous mount.
      // A cancelled mount should still mark the spinner cleared so that ANY
      // re-mount doesn't inherit a stuck loading state. The cancelled flag
      // only guards expensive setState that would queue work on an unmounted
      // component (React itself ignores those, but we keep them out for hygiene).
      setLoading(false);
      if (cancelled) return;
      if (bundle) {
        setProfile(bundle.profile || bundle);
        setQuote(bundle.quote || null);
        setTea(bundle.tea || null);
        setNews(bundle.news || []);
      }
      const bars = ohlcvData?.bars || [];
      if (bars.length) {
        setOhlcv(bars);
        // The backend returns {earnings: [{symbol, earningsDate, daysUntil}]} —
        // this read only ever looked for date/reportDate/fiscalDateEnding, so
        // no earnings marker had ever rendered on the chart.
        const earningsDates = (earningsData?.earnings || earningsData || []).map(e => e.earningsDate || e.date || e.reportDate || e.fiscalDateEnding).filter(Boolean);
        setEvents(computeEvents(bars, earningsDates));
      }
      if (intradayData?.bars) setIntraday(intradayData.bars);
      if (ivData?.history) setIvHist(ivData.history);
      if (regimeData) setRegime(regimeData);
      const exps = expData?.expirations || [];
      setExpirations(exps);
      if (exps.length) {
        setSelectedExpiry(exps[0]);
        loadChain(symbol, exps[0]);
      }
    };

    // ── Cache-first load ──────────────────────────────────────────────────────
    const cached = getCached(symbol);
    if (cached) {
      // Warm cache hit — apply immediately, no spinner
      const { bundle, ohlcv: ov, intraday: id, iv, regime, expirations: ex, earnings } = cached;
      applyData(bundle, ov, id, iv, regime, ex, earnings);
    } else {
      setLoading(true);
      const settle = p => p.then(r => r.data).catch(() => null);

      // Reuse in-flight prefetch if one is already running (avoids duplicate network calls)
      const inflight = getInflight(symbol);
      const dataPromise = inflight || Promise.all([
        settle(axios.get(`${API}/market/symbol-bundle?symbol=${symbol}&include_news=true&news_limit=10`)),
        settle(axios.get(`${API}/market/ohlcv?symbol=${symbol}&days=90`)),
        settle(axios.get(`${API}/market/intraday?symbol=${symbol}&interval=5m&range_val=1d`)),
        settle(axios.get(`${API}/iv/history/full?symbol=${symbol}&days=30`)),
        settle(axios.get(`${API}/zi/regime?symbol=${symbol}`)),
        settle(axios.get(`${API}/market/expirations?symbol=${symbol}`)),
        settle(axios.get(`${API}/earnings?symbols=${symbol}`)),
      ]).then(([bundle, ohlcvData, intradayData, ivData, regimeData, expData, earningsData]) => {
        const data = { bundle, ohlcv: ohlcvData, intraday: intradayData, iv: ivData, regime: regimeData, expirations: expData, earnings: earningsData };
        hydrate(symbol, data);
        return data;
      });

      dataPromise.then(data => {
        if (!data) { setLoading(false); return; }
        const { bundle, ohlcv: ov, intraday: id, iv, regime, expirations: ex, earnings } = data;
        applyData(bundle, ov, id, iv, regime, ex, earnings);
      }).catch(() => setLoading(false));
    }

    // Live quote refresh every 5s
    quoteTimerRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/market/quotes?symbols=${symbol}`);
        const q = Array.isArray(r.data) ? r.data[0] : null;
        if (q) setQuote(prev => ({ ...prev, ...q }));
      } catch {}
    }, 5000);

    return () => { cancelled = true; if (quoteTimerRef.current) clearInterval(quoteTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const loadChain = useCallback(async (sym, expiry) => {
    setChainLoading(true);
    setChainScore({});
    try {
      const r = await axios.get(`${API}/market/chain?symbol=${sym}&expiry=${expiry}`);
      setChain(r.data);
      // Fetch Greeks for ATM calls and puts
      const price = quote?.price || quote?.bid || 0;
      const osis = [
        ...(r.data?.calls || []).filter(c => Math.abs(c.strike - price) < price * 0.05).slice(0, 4).map(c => c.osi),
        ...(r.data?.puts  || []).filter(c => Math.abs(c.strike - price) < price * 0.05).slice(0, 4).map(c => c.osi),
      ].filter(Boolean);
      if (osis.length) {
        try {
          const gr = await axios.post(`${API}/market/greeks/batch`, { osiSymbols: osis });
          setGreeks(Array.isArray(gr.data) ? gr.data : []);
        } catch {}
      }
      // Real Zi chain-score — same ULTRA_GEM/GEM/NEUTRAL/AVOID classes and
      // real `diamond` flag AutopilotPanel/OptionsHubTab already use, not an
      // invented composite. Scored contracts nearest the money (server caps
      // at 30 total).
      if (price) {
        const calls = (r.data?.calls || []).map(c => ({ ...c, type: "call" }));
        const puts  = (r.data?.puts  || []).map(c => ({ ...c, type: "put" }));
        const nearMoney = [...calls, ...puts]
          .sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))
          .slice(0, 30);
        if (nearMoney.length) {
          try {
            const sr = await axios.post(`${API}/zi/chain-score`, { symbol: sym, underlyingPrice: price, contracts: nearMoney });
            const map = {};
            (Array.isArray(sr.data) ? sr.data : []).forEach(s => { map[`${s.type}-${s.strike}`] = s; });
            setChainScore(map);
          } catch {}
        }
      }
    } catch {} finally { setChainLoading(false); }
  }, [quote?.price, quote?.bid]);

  const loadAnalysis = useCallback(async () => {
    if (analysisCalledRef.current) return;
    analysisCalledRef.current = true;
    setAnalysisLoading(true);
    setAnalysisError(null);
    try {
      const r = await axios.post(`${API}/omega/v2/analyze-symbol`, { symbol, market_rows: [], account: {}, positions: [] });
      setAnalysis(r.data);
    } catch (e) {
      setAnalysisError(e?.response?.data?.detail || e.message || "Zi analysis failed");
      analysisCalledRef.current = false;
    } finally { setAnalysisLoading(false); }
  }, [symbol]);

  const loadDeepRead = useCallback(async () => {
    if (deepReadCalledRef.current) return;
    deepReadCalledRef.current = true;
    setDeepReadLoading(true);
    setDeepReadError(null);
    try {
      const detailRes = await axios.get(`${API}/tea/detail?symbol=${symbol}`).catch(() => null);
      const articles  = detailRes?.data?.articles || news;
      const r = await axios.post(`${API}/tea/deep-read`, { symbol, tea, articles, profile });
      setDeepRead(r.data);
      if (detailRes?.data?.articles) setTeaDetail(detailRes.data);
    } catch (e) {
      setDeepReadError(e?.response?.data?.detail || e.message || "Deep read failed");
      deepReadCalledRef.current = false;
    } finally { setDeepReadLoading(false); }
  }, [symbol, tea, news, profile]);

  useEffect(() => {
    if (tab === "analysis") { loadDeepRead(); loadAnalysis(); }
    if (tab === "news")     { loadDeepRead(); }
  }, [tab, loadDeepRead, loadAnalysis]);

  // ── Quote state ───────────────────────────────────────────────────────────────
  const price    = quote?.price ?? quote?.last_price ?? profile?.price ?? null;
  const prevClose = profile?.prevClose ?? null;
  const change   = price != null && prevClose != null ? price - prevClose : null;
  const changePct = prevClose ? (change / prevClose) * 100 : null;
  const isUp     = change == null ? null : change >= 0;
  const priceColor = isUp == null ? "var(--text)" : isUp ? "#4ade80" : "#f87171";

  // ── Chart data builders (memoized — only recompute when underlying data changes, not on every quote tick) ──
  const priceChartData = useMemo(() => {
    if (!ohlcv?.length) return null;
    const days = chartRange === "1W" ? 7 : chartRange === "1M" ? 30 : chartRange === "3M" ? 90 : 60;
    const bars = ohlcv.slice(-days);
    return {
      labels: bars.map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
      datasets: [{ label: symbol, data: bars.map(b => b.c), borderColor: priceColor, backgroundColor: priceColor + "14", fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3 }],
    };
  }, [ohlcv, chartRange, priceColor, symbol]);

  const intradayChartData = useMemo(() => {
    if (!intraday?.length) return null;
    return {
      labels: intraday.map(b => new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
      datasets: [{ label: "Price", data: intraday.map(b => b.c), borderColor: priceColor, backgroundColor: priceColor + "14", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2 }],
    };
  }, [intraday, priceColor]);

  const ivChartData = useMemo(() => {
    if (!ivHist?.length) return null;
    return {
      labels: ivHist.map(h => h.date),
      datasets: [{ label: "IV %", data: ivHist.map(h => parseFloat((h.iv * 100).toFixed(1))), borderColor: "#fbbf24", backgroundColor: "rgba(251,191,36,0.08)", fill: true, pointRadius: 2, borderWidth: 1.5, tension: 0.3 }],
    };
  }, [ivHist]);

  // ── Hero chart with event dots ───────────────────────────────────────────────
  const heroChartData = useMemo(() => {
    if (!ohlcv?.length) return null;
    const bars = ohlcv.slice(-60);
    const eventMap = {};
    events.forEach(ev => { eventMap[ev.date] = ev; });
    const labels  = bars.map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    const evDates = bars.map(b => new Date(b.t).toISOString().slice(0, 10));
    return {
      labels,
      datasets: [
        {
          label: "Price",
          data: bars.map(b => b.c),
          borderColor: priceColor, backgroundColor: priceColor + "12",
          fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3,
        },
        {
          label: "Events",
          data: bars.map(b => { const d = new Date(b.t).toISOString().slice(0, 10); return eventMap[d] ? b.c : null; }),
          showLine: false,
          pointRadius: evDates.map(d => eventMap[d] ? 8 : 0),
          pointHoverRadius: evDates.map(d => eventMap[d] ? 11 : 0),
          pointBackgroundColor: evDates.map(d => eventMap[d]?.color || "transparent"),
          pointBorderColor: "#0f172a",
          pointBorderWidth: 2,
          fill: false,
        },
      ],
    };
  }, [ohlcv, events, priceColor]);

  const heroChartOpts = useMemo(() => ({
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index", intersect: false,
        backgroundColor: "rgba(15,23,42,0.97)",
        borderColor: priceColor + "55", borderWidth: 1,
        titleColor: "#94a3b8", bodyColor: "#e2e8f0", padding: 12,
        callbacks: {
          title: items => items[0]?.label || "",
          label: item => item.datasetIndex === 0 ? ` $${fmt(item.raw, 2)}` : "",
          afterBody: items => {
            const bar = ohlcv?.slice(-60)[items[0]?.dataIndex];
            if (!bar) return [];
            const d = new Date(bar.t).toISOString().slice(0, 10);
            const ev = events.find(e => e.date === d);
            return ev ? [`${ev.icon}  ${ev.insight}`] : [];
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: "#475569", font: { size: 9 }, maxTicksLimit: 7 }, grid: { color: "rgba(255,255,255,0.04)" } },
      y: { position: "right", ticks: { color: "#475569", font: { size: 9 }, callback: v => `$${v}` }, grid: { color: "rgba(255,255,255,0.04)" } },
    },
  }), [priceColor, ohlcv, events]);

  // ── Trade actions ─────────────────────────────────────────────────────────────
  const isCrypto = CRYPTO_TICKERS.has((symbol || "").toUpperCase());
  const handleUnderlyingTrade = (mode, qty) => {
    setQuickTrade({
      position: { symbol, ticker: symbol, quantity: qty, lastPrice: price, avgCost: price, type: isCrypto ? "CRYPTO" : "EQUITY" },
      mode: mode === "sell" ? "sell" : "buy_more",
    });
  };
  const handleOptionTrade = (contractForTrade, qty) => {
    if (!contractForTrade) return;
    const mid = contractForTrade.bid != null && contractForTrade.ask != null ? (contractForTrade.bid + contractForTrade.ask) / 2 : contractForTrade.last || 0;
    setQuickTrade({
      position: { symbol: contractForTrade.osi, ticker: symbol, quantity: qty, lastPrice: mid, avgCost: mid, type: "OPTION" },
      mode: "buy_more",
    });
  };

  // ── Option chain helpers ──────────────────────────────────────────────────────
  const underlying = price || 0;
  const calls = useMemo(() => chain?.calls || [], [chain]);
  const puts  = useMemo(() => chain?.puts  || [], [chain]);
  const atmSlice = (arr, n = 12) => {
    if (!underlying) return arr.slice(0, n);
    return [...arr].sort((a, b) => Math.abs(a.strike - underlying) - Math.abs(b.strike - underlying)).slice(0, n).sort((a, b) => a.strike - b.strike);
  };

  // Max Pain / Put-Call OI Ratio — no backend equivalent exists for either
  // (confirmed: no maxPain/putCallRatio field anywhere in the API), so these
  // are computed here, client-side, over the real chain's real OI — the
  // standard OI-weighted-payout formula, not an approximation.
  const { maxPain, putCallRatio } = useMemo(() => {
    if (!calls.length && !puts.length) return { maxPain: null, putCallRatio: null };
    const strikes = [...new Set([...calls.map(c => c.strike), ...puts.map(c => c.strike)])].sort((a, b) => a - b);
    let bestStrike = null, bestPayout = Infinity;
    strikes.forEach(settle => {
      let payout = 0;
      calls.forEach(c => { if (settle > c.strike) payout += (settle - c.strike) * (c.oi || 0); });
      puts.forEach(p => { if (settle < p.strike) payout += (p.strike - settle) * (p.oi || 0); });
      if (payout < bestPayout) { bestPayout = payout; bestStrike = settle; }
    });
    const totalCallOi = calls.reduce((s, c) => s + (c.oi || 0), 0);
    const totalPutOi  = puts.reduce((s, c) => s + (c.oi || 0), 0);
    return { maxPain: bestStrike, putCallRatio: totalCallOi > 0 ? totalPutOi / totalCallOi : null };
  }, [calls, puts]);

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 48, height: 48, border: "3px solid var(--border)", borderTopColor: "#60a5fa", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 14, color: "var(--muted)", fontWeight: 600 }}>Loading {symbol}…</div>
    </div>
  );

  const name = profile?.longName || profile?.shortName || profile?.name || symbol;
  const sector = profile?.sector || "";
  const industry = profile?.industry || "";
  const marketCap = profile?.marketCap;
  const w52h = profile?.fiftyTwoWeekHigh;
  const w52l = profile?.fiftyTwoWeekLow;
  const dayH = profile?.dayHigh;
  const dayL = profile?.dayLow;
  const avgVol = profile?.averageVolume;
  const rsi = regime?.indicators?.rsi;
  const annualVol = regime?.currentVol;
  const logo = profile?.logo_url || profile?.logoUrl || profile?.logo;
  const regimeName = regime?.regime || regime?.signal;

  return (
    <div data-testid="ticker-card" style={{ color: "var(--text)", maxWidth: 1100, margin: "0 auto" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <div style={{ borderRadius: 20, background: "linear-gradient(160deg, #0a0f1e 0%, #111827 60%, #0a0f1e 100%)", border: `1px solid ${priceColor}22`, boxShadow: `0 24px 64px ${priceColor}14`, marginBottom: 20, overflow: "hidden", position: "relative", willChange: "transform", transform: "translateZ(0)" }}>
        {/* Ambient glow */}
        <div style={{ position: "absolute", top: -80, right: -80, width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, ${priceColor}18, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${priceColor}0a, transparent 70%)`, pointerEvents: "none" }} />

        {/* ── Identity + price row ── */}
        <div style={{ padding: "20px 24px 14px", display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap", position: "relative" }}>
          {/* Logo + symbol */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 200 }}>
            {logo && <img src={logo} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: "contain", background: "rgba(255,255,255,0.07)", padding: 5, flexShrink: 0 }} onError={e => e.target.style.display = "none"} />}
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", letterSpacing: -0.5, lineHeight: 1 }}>{symbol}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 3 }}>{name}{sector ? ` · ${sector}` : ""}</div>
            </div>
          </div>

          {/* Price + change */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 38, fontWeight: 900, color: priceColor, letterSpacing: -1 }}>${fmt(price, 2)}</span>
            {change != null && (
              <span style={{ fontSize: 16, fontWeight: 700, color: priceColor }}>
                {change > 0 ? "+" : ""}{fmt(change, 2)} ({pct(changePct)})
              </span>
            )}
          </div>

          {/* Badges */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
              <span style={{ color: "#4ade80", fontWeight: 700 }}>${fmt(quote?.bid, 2)}</span> / <span style={{ color: "#f87171", fontWeight: 700 }}>${fmt(quote?.ask, 2)}</span>
              <span style={{ marginLeft: 8 }}>Vol {quote?.volume ? Number(quote.volume).toLocaleString() : "—"}</span>
            </div>
            {regimeName && (
              <div style={{ padding: "3px 9px", borderRadius: 6, background: regimeName === "bull" ? "rgba(74,222,128,0.2)" : regimeName === "bear" ? "rgba(248,113,113,0.2)" : "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)", fontSize: 10, fontWeight: 800, color: regimeName === "bull" ? "#4ade80" : regimeName === "bear" ? "#f87171" : "#94a3b8" }}>
                {String(regimeName).toUpperCase()}
              </div>
            )}
            {tea && <TeaBadge score={tea.teaScore} sentiment={tea.sentiment} />}
            {/* 52W range pills */}
            {(w52h || w52l) && (
              <div style={{ display: "flex", gap: 6 }}>
                {w52h && <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: "rgba(74,222,128,0.1)", color: "#4ade80" }}>52W H ${fmt(w52h, 0)}</div>}
                {w52l && <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: "rgba(248,113,113,0.1)", color: "#f87171" }}>52W L ${fmt(w52l, 0)}</div>}
              </div>
            )}
          </div>

          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8", fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
            Back
          </button>
        </div>

        {/* ── Live chart with event dots ── */}
        <div style={{ height: 200, position: "relative", overflow: "hidden" }}>
          {heroChartData
            ? <Line data={heroChartData} options={heroChartOpts} />
            : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(255,255,255,0.2)", fontSize: 12 }}>Chart loading…</div>
          }
        </div>

        {/* ── Event dots legend ── */}
        {events.length > 0 && (
          <div style={{ padding: "10px 20px 18px", display: "flex", gap: 8, overflowX: "auto" }}>
            {events.map((ev, i) => {
              const isSelected = selectedEvent?.t === ev.t;
              return (
                <button key={i} onClick={() => setSelectedEvent(isSelected ? null : ev)}
                  style={{
                    padding: "6px 12px", borderRadius: 10, border: `1.5px solid ${ev.color}${isSelected ? "ff" : "55"}`,
                    background: isSelected ? ev.color + "22" : "rgba(255,255,255,0.04)",
                    color: "#fff", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                    display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
                  }}>
                  <span style={{ fontSize: 13 }}>{ev.icon}</span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: ev.color }}>{ev.label}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>{new Date(ev.t).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Expanded event insight panel ── */}
        {selectedEvent && (
          <div style={{ margin: "0 20px 18px", padding: "14px 16px", borderRadius: 12, background: selectedEvent.color + "14", border: `1px solid ${selectedEvent.color}44`, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ fontSize: 22, flexShrink: 0 }}>{selectedEvent.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: selectedEvent.color, marginBottom: 4 }}>
                {selectedEvent.label} — {new Date(selectedEvent.t).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · ${fmt(selectedEvent.price, 2)}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>{selectedEvent.insight}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 6 }}>
                Vol {selectedEvent.volRatio.toFixed(1)}× avg · Price {selectedEvent.change > 0 ? "+" : ""}{selectedEvent.change.toFixed(2)}% that day
              </div>
            </div>
            <button onClick={() => setSelectedEvent(null)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 18, flexShrink: 0, padding: 2 }}>×</button>
          </div>
        )}
      </div>

      {/* ══ TABS ══════════════════════════════════════════════════════════════ */}
      <div style={{ display: "flex", gap: 4, marginBottom: 18, overflowX: "auto", paddingBottom: 2 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "9px 18px", borderRadius: 12, border: "none", cursor: "pointer", whiteSpace: "nowrap",
            fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6,
            background: tab === t.id ? priceColor : "var(--layer2)",
            color: tab === t.id ? (isUp == null ? "#0f172a" : "#0f172a") : "var(--muted)",
            boxShadow: tab === t.id ? `0 4px 14px ${priceColor}44` : "none",
            transition: "all 0.15s",
          }}>
            <span style={{ fontSize: 13 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ══ OVERVIEW ══════════════════════════════════════════════════════════ */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Key stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            <Stat label="Market Cap"    value={fmtB(marketCap)} />
            <Stat label="Avg Volume"    value={avgVol ? Number(avgVol).toLocaleString() : "—"} />
            <Stat label="RSI (14)"      value={rsi ? fmt(rsi, 1) : "—"} color={rsi > 70 ? "#f87171" : rsi < 30 ? "#4ade80" : "var(--text)"} sub={rsi > 70 ? "Overbought" : rsi < 30 ? "Oversold" : "Neutral"} />
            <Stat label="Ann. Vol"      value={annualVol ? fmt(annualVol, 1) + "%" : "—"} />
            <Stat label="Prev Close"    value={fmtC(prevClose)} />
            <Stat label="Spread"        value={quote?.bid != null && quote?.ask != null ? fmtC(quote.ask - quote.bid) : "—"} />
          </div>

          {/* Company + Trading info */}
          {(profile?.longBusinessSummary || profile?.description) && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>About</div>
              <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.7, display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {profile.longBusinessSummary || profile.description}
              </div>
              {sector && <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "rgba(96,165,250,0.12)", color: "#60a5fa", fontWeight: 600 }}>{sector}</span>
                {industry && <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "var(--layer2)", color: "var(--muted)", fontWeight: 500 }}>{industry}</span>}
              </div>}
            </div>
          )}

          {/* Trading capabilities */}
          {profile?.trading && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Trading Capabilities</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { label: "Trading",    val: profile.trading },
                  { label: "Options",    val: profile.optionTrading },
                  { label: "Spreads",    val: profile.optionSpreadTrading },
                  { label: "Fractional", val: profile.fractionalTrading },
                ].map(cap => (
                  <div key={cap.label} style={{ padding: "6px 12px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 11 }}>
                    <span style={{ color: "var(--muted)" }}>{cap.label}: </span>
                    <span style={{ color: "#4ade80", fontWeight: 700 }}>{String(cap.val || "").replace("Trading.", "").replace("_", " ") || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TEA card */}
          {tea && (
            <div className="card" style={{ borderColor: tea.sentiment === "bullish" ? "rgba(74,222,128,0.25)" : tea.sentiment === "bearish" ? "rgba(248,113,113,0.25)" : "var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>TEA Score — Sentiment Analysis</div>
                <TeaBadge score={tea.teaScore} sentiment={tea.sentiment} />
              </div>
              {/* Score bar */}
              <div style={{ height: 6, borderRadius: 3, background: "var(--layer2)", marginBottom: 10, overflow: "hidden" }}>
                <div style={{ height: "100%", width: tea.teaScore == null ? "0%" : `${tea.teaScore}%`, borderRadius: 3, background: tea.teaScore == null ? "#64748b" : tea.teaScore >= 70 ? "#4ade80" : tea.teaScore >= 45 ? "#fbbf24" : "#f87171", transition: "width 0.6s ease" }} />
              </div>
              {tea.summary && <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, marginBottom: 12 }}>{tea.summary}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {(tea.headlines || []).slice(0, 5).map((h, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--dim)", padding: "5px 8px", borderRadius: 6, background: "var(--layer2)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{ color: tea.sentiment === "bullish" ? "#4ade80" : tea.sentiment === "bearish" ? "#f87171" : "#fbbf24", fontWeight: 700, flexShrink: 0 }}>●</span>
                    {h}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Trade the underlying ── */}
          <TradeCard symbol={symbol} price={price} onTrade={handleUnderlyingTrade} />
        </div>
      )}

      {/* ══ CHART ═════════════════════════════════════════════════════════════ */}
      {tab === "chart" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Mode + Range toggles */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "candle",   label: "Candles" },
                { id: "intraday", label: "Intraday" },
                { id: "price",    label: "Line" },
                { id: "iv",       label: "IV" },
              ].map(m => (
                <button key={m.id} onClick={() => setChartMode(m.id)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none", background: chartMode === m.id ? priceColor : "var(--layer2)", color: chartMode === m.id ? "#0f172a" : "var(--muted)" }}>
                  {m.label}
                </button>
              ))}
            </div>
            {(chartMode === "candle" || chartMode === "price") && (
              <div style={{ display: "flex", gap: 6 }}>
                {["1W", "1M", "3M", "ALL"].map(r => (
                  <button key={r} onClick={() => setChartRange(r)} style={{ padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none", background: "transparent", color: chartRange === r ? "var(--text)" : "var(--dim)", borderBottom: chartRange === r ? `2px solid ${priceColor}` : "2px solid transparent" }}>
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Candlestick chart — full-bleed dark card */}
          {chartMode === "candle" && (
            <div className="card" style={{ padding: "16px 16px 10px", background: "var(--layer2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{symbol} · Candlestick</span>
                  {change != null && <span style={{ fontSize: 12, color: priceColor, fontWeight: 600 }}>{change > 0 ? "+" : ""}{fmt(change, 2)} ({pct(changePct)})</span>}
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--muted)" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "#4ade80", display: "inline-block" }} /> Bullish</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "#f87171", display: "inline-block" }} /> Bearish</span>
                  {events.length > 0 && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ color: "#a78bfa" }}>⭐</span> Events</span>}
                </div>
              </div>
              {ohlcv?.length ? (
                <CandleChart bars={ohlcv} events={events} range={chartRange} height={360} />
              ) : (
                <div style={{ height: 360, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 13 }}>No OHLCV data</div>
              )}
            </div>
          )}

          {/* Line charts */}
          {chartMode !== "candle" && (
            <div className="card" style={{ padding: 20 }}>
              {chartMode === "price" && (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{symbol} Price</span>
                    {change != null && <span style={{ fontSize: 13, color: priceColor, fontWeight: 600 }}>{change > 0 ? "+" : ""}{fmt(change, 2)} ({pct(changePct)})</span>}
                  </div>
                  <div style={{ height: 280, overflow: "hidden" }}>
                    {priceChartData
                      ? <Line data={priceChartData} options={CHART_OPTS(priceColor)} />
                      : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 13 }}>No price data</div>
                    }
                  </div>
                </>
              )}
              {chartMode === "intraday" && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{symbol} · Today (5m)</div>
                  <div style={{ height: 280, overflow: "hidden" }}>
                    {intradayChartData
                      ? <Line data={intradayChartData} options={CHART_OPTS(priceColor)} />
                      : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 13 }}>No intraday data</div>
                    }
                  </div>
                </>
              )}
              {chartMode === "iv" && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{symbol} Implied Volatility (30d)</div>
                  <div style={{ height: 280, overflow: "hidden" }}>
                    {ivChartData
                      ? <Line data={ivChartData} options={{ ...CHART_OPTS("#fbbf24"), scales: { ...CHART_OPTS("#fbbf24").scales, y: { ...CHART_OPTS("#fbbf24").scales.y, ticks: { ...CHART_OPTS("#fbbf24").scales.y.ticks, callback: v => v + "%" } } } }} />
                      : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--muted)", fontSize: 13 }}>IV history builds daily</div>
                    }
                  </div>
                </>
              )}
            </div>
          )}

          {/* Volume bar chart — only for line modes (candle has built-in volume) */}
          {chartMode !== "candle" && ohlcv?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Volume</div>
              <div style={{ height: 100, overflow: "hidden" }}>
                <Bar
                  data={{ labels: ohlcv.slice(-30).map(b => new Date(b.t).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })), datasets: [{ data: ohlcv.slice(-30).map(b => b.v || 0), backgroundColor: ohlcv.slice(-30).map(b => b.c >= (b.o || b.c) ? "rgba(74,222,128,0.6)" : "rgba(248,113,113,0.6)"), borderRadius: 3, borderSkipped: false }] }}
                  options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: "rgba(15,23,42,0.95)", callbacks: { label: ctx => Number(ctx.raw).toLocaleString() } } }, scales: { x: { ticks: { color: "#64748b", font: { size: 8 }, maxTicksLimit: 8 }, grid: { display: false } }, y: { ticks: { color: "#64748b", font: { size: 8 }, callback: v => v >= 1e6 ? (v / 1e6).toFixed(0) + "M" : v >= 1e3 ? (v / 1e3).toFixed(0) + "K" : v }, grid: { color: "var(--grid)" } } } }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ OPTIONS ═══════════════════════════════════════════════════════════ */}
      {tab === "options" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Expiry selector */}
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {expirations.slice(0, 12).map(exp => (
              <button key={exp} onClick={() => { setSelectedExpiry(exp); loadChain(symbol, exp); }} style={{ padding: "7px 16px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", border: "none", background: selectedExpiry === exp ? "#a78bfa" : "var(--layer2)", color: selectedExpiry === exp ? "#0f172a" : "var(--muted)" }}>
                {exp}
              </button>
            ))}
          </div>

          {!chainLoading && chain && (maxPain != null || putCallRatio != null) && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {maxPain != null && (
                <div style={{ fontSize: 11, padding: "6px 12px", borderRadius: 8, background: "var(--layer2)", color: "var(--muted)" }}>
                  Max Pain <span style={{ color: "var(--text)", fontWeight: 700 }}>${fmt(maxPain, 0)}</span>
                  <span style={{ fontSize: 9, color: "var(--dim)", marginLeft: 6 }}>client-computed</span>
                </div>
              )}
              {putCallRatio != null && (
                <div style={{ fontSize: 11, padding: "6px 12px", borderRadius: 8, background: "var(--layer2)", color: "var(--muted)" }}>
                  Put/Call OI <span style={{ color: putCallRatio >= 1.15 ? "#f87171" : putCallRatio <= 0.75 ? "#4ade80" : "var(--text)", fontWeight: 700 }}>{putCallRatio.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {chainLoading && <div style={{ textAlign: "center", padding: 32, color: "var(--muted)", fontSize: 13 }}>Loading chain…</div>}

          {!chainLoading && chain && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {/* Calls */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#4ade80", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80", display: "inline-block" }} />
                  CALLS · {calls.length}
                  {underlying > 0 && <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--muted)", fontWeight: 400 }}>underlying ${fmt(underlying, 2)}</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {atmSlice(calls).map((c, i) => <ContractCard key={i} c={c} type="call" underlying={underlying} onSelect={setSelectedContract} scoreInfo={chainScore[`call-${c.strike}`]} />)}
                </div>
              </div>
              {/* Puts */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#f87171", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f87171", display: "inline-block" }} />
                  PUTS · {puts.length}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {atmSlice(puts).map((c, i) => <ContractCard key={i} c={c} type="put" underlying={underlying} onSelect={setSelectedContract} scoreInfo={chainScore[`put-${c.strike}`]} />)}
                </div>
              </div>
            </div>
          )}

          {selectedContract && (
            <OptionDetailModal
              contract={selectedContract}
              underlying={underlying}
              greeks={greeks}
              expiry={selectedExpiry}
              symbol={symbol}
              onClose={() => setSelectedContract(null)}
              onTrade={handleOptionTrade}
            />
          )}
        </div>
      )}

      {/* ══ GREEKS ════════════════════════════════════════════════════════════ */}
      {tab === "greeks" && (
        <div>
          {greeks.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: "var(--muted)", fontSize: 13 }}>
              {expirations.length ? "Greeks load with the option chain. Visit the Options tab first." : "No options data available for this symbol."}
            </div>
          )}
          {greeks.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>ATM Contracts · {selectedExpiry}</div>
              {greeks.map((g, i) => {
                const parsed = g.osi?.match(/^([A-Z]+)(\d{6})([CP])(\d+)$/);
                const type = parsed?.[3] === "C" ? "CALL" : "PUT";
                const strike = parsed ? parseInt(parsed[4]) / 1000 : "?";
                return (
                  <div key={i} className="card" style={{ borderColor: type === "CALL" ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.2)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, padding: "3px 8px", borderRadius: 6, background: type === "CALL" ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)", color: type === "CALL" ? "#4ade80" : "#f87171" }}>{type}</span>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>${strike}</span>
                      <span style={{ fontSize: 11, color: "var(--dim)", fontFamily: "monospace" }}>{g.osi}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                      {[
                        { l: "Delta Δ", v: g.delta != null ? fmt(g.delta, 4) : "—", color: g.delta > 0 ? "#4ade80" : "#f87171", desc: "Price sensitivity" },
                        { l: "Gamma Γ", v: g.gamma != null ? fmt(g.gamma, 4) : "—", color: "#fbbf24", desc: "Delta rate of change" },
                        { l: "Theta Θ", v: g.theta != null ? fmt(g.theta, 4) : "—", color: "#f87171", desc: "Time decay/day" },
                        { l: "Vega ν",  v: g.vega  != null ? fmt(g.vega,  4) : "—", color: "#a78bfa", desc: "IV sensitivity" },
                        { l: "IV",     v: g.iv    != null ? fmt(g.iv * 100, 1) + "%" : "—", color: "#38bdf8", desc: "Implied volatility" },
                      ].map(gr => (
                        <div key={gr.l} style={{ textAlign: "center", padding: "10px 6px", borderRadius: 10, background: "var(--layer2)" }}>
                          <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 4 }}>{gr.l}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: gr.color }}>{gr.v}</div>
                          <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 3 }}>{gr.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ NEWS ══════════════════════════════════════════════════════════════ */}
      {tab === "news" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Omega summary banner */}
          {deepRead ? (
            <div className="card" style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.10), rgba(96,165,250,0.05))", borderColor: "rgba(167,139,250,0.25)", padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.5 }}>✦ OMEGA INTELLIGENCE</span>
                {deepRead.verdict && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                    background: (deepRead.verdict.includes("BUY") || deepRead.verdict.includes("BULL")) ? "rgba(74,222,128,0.15)" : (deepRead.verdict.includes("SELL") || deepRead.verdict.includes("BEAR")) ? "rgba(248,113,113,0.15)" : "rgba(167,139,250,0.15)",
                    color: (deepRead.verdict.includes("BUY") || deepRead.verdict.includes("BULL")) ? "#4ade80" : (deepRead.verdict.includes("SELL") || deepRead.verdict.includes("BEAR")) ? "#f87171" : "#a78bfa",
                    border: "1px solid",
                    borderColor: (deepRead.verdict.includes("BUY") || deepRead.verdict.includes("BULL")) ? "rgba(74,222,128,0.3)" : (deepRead.verdict.includes("SELL") || deepRead.verdict.includes("BEAR")) ? "rgba(248,113,113,0.3)" : "rgba(167,139,250,0.3)",
                  }}>{deepRead.verdict.replace(/_/g, " ")}</span>
                )}
              </div>
              {deepRead.summary && <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6 }}>{deepRead.summary}</div>}
              {tea && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <TeaBadge score={tea.teaScore} sentiment={tea.sentiment} />
                  {tea.summary && <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4 }}>{tea.summary}</span>}
                </div>
              )}
            </div>
          ) : deepReadLoading ? (
            <div className="card" style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(96,165,250,0.03))", borderColor: "rgba(167,139,250,0.15)", padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 16, height: 16, border: "2px solid rgba(167,139,250,0.3)", borderTopColor: "#a78bfa", borderRadius: "50%", flexShrink: 0,
                  animation: "spin 0.8s linear infinite" }} />
                <span style={{ fontSize: 12, color: "#a78bfa", fontWeight: 600 }}>Omega is reading articles...</span>
              </div>
            </div>
          ) : tea ? (
            <div className="card" style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))", borderColor: "rgba(167,139,250,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>News Sentiment</span>
                <TeaBadge score={tea.teaScore} sentiment={tea.sentiment} />
              </div>
              {tea.summary && <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6 }}>{tea.summary}</div>}
            </div>
          ) : null}

          {news.length === 0 && <div style={{ textAlign: "center", padding: 40, color: "var(--muted)", fontSize: 13 }}>No news found.</div>}
          {(teaDetail?.articles || news).map((article, i) => {
            const title = article.title || article;
            const omegaHs = deepRead?.headline_sentiments?.find(h => h.title?.slice(0, 50) === title?.slice(0, 50));
            const artSent = omegaHs?.sentiment || "neutral";
            const sentColor = s => s === "bullish" || s === "positive" ? "#4ade80" : s === "bearish" || s === "negative" ? "#f87171" : "#94a3b8";
            const lvl = omegaHs?.impact_level || omegaHs?.impact;
            const impactColor = l => l === "high" ? "#f87171" : l === "medium" ? "#fbbf24" : "#64748b";
            const impactBg = l => l === "high" ? "rgba(248,113,113,0.1)" : l === "medium" ? "rgba(251,191,36,0.1)" : "rgba(100,116,139,0.08)";
            return (
              <div key={i} className="card" style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 4, flexShrink: 0, minHeight: 40, borderRadius: 2, background: sentColor(artSent), alignSelf: "stretch" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", lineHeight: 1.45, marginBottom: 4 }}>{title}</div>
                    {omegaHs?.reason && (
                      <div style={{ fontSize: 11, color: "#a78bfa", lineHeight: 1.4, marginBottom: 6, fontStyle: "italic" }}>
                        ✦ {omegaHs.reason}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {article.source && <span style={{ fontSize: 10, color: "var(--dim)" }}>{article.source}</span>}
                      {article.pubDate && <span style={{ fontSize: 10, color: "var(--dim)" }}>· {article.pubDate?.slice(0, 16)}</span>}
                      {omegaHs && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: sentColor(artSent), padding: "1px 6px", borderRadius: 10,
                          background: artSent === "bullish" || artSent === "positive" ? "rgba(74,222,128,0.1)" : artSent === "bearish" || artSent === "negative" ? "rgba(248,113,113,0.1)" : "rgba(148,163,184,0.08)",
                          border: `1px solid ${sentColor(artSent)}40` }}>
                          {artSent.toUpperCase()}
                        </span>
                      )}
                      {lvl && (
                        <span style={{ fontSize: 9, fontWeight: 600, color: impactColor(lvl), padding: "1px 6px", borderRadius: 10,
                          background: impactBg(lvl), border: `1px solid ${impactColor(lvl)}40` }}>
                          {lvl.toUpperCase()} IMPACT
                        </span>
                      )}
                    </div>
                  </div>
                  {article.link && (
                    <a href={article.link} target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", fontSize: 18, textDecoration: "none", flexShrink: 0 }}>↗</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ AI ANALYSIS ═══════════════════════════════════════════════════════ */}
      {tab === "analysis" && (() => {
        // null means the catalyst could not be graded — NOT a score of 0,
        // which the ring would paint as the most bearish reading there is.
        const teaScore = tea?.teaScore ?? null;
        const teaGraded = teaScore != null;
        const teaColor = !teaGraded ? "#64748b"
          : teaScore >= 70 ? "#4ade80" : teaScore >= 45 ? "#fbbf24" : "#f87171";
        const circumference = 2 * Math.PI * 36;
        const dashOffset = teaGraded ? circumference - (teaScore / 100) * circumference : circumference;

        const sentimentColor = s => s === "bullish" || s === "positive" ? "#4ade80" : s === "bearish" || s === "negative" ? "#f87171" : "#94a3b8";
        const impactBg = lvl => lvl === "high" ? "rgba(248,113,113,0.1)" : lvl === "medium" ? "rgba(251,191,36,0.1)" : "rgba(148,163,184,0.08)";
        const impactBorder = lvl => lvl === "high" ? "rgba(248,113,113,0.3)" : lvl === "medium" ? "rgba(251,191,36,0.3)" : "rgba(148,163,184,0.15)";
        const impactColor = lvl => lvl === "high" ? "#f87171" : lvl === "medium" ? "#fbbf24" : "#94a3b8";

        const isLoading = deepReadLoading || analysisLoading;
        const verdictColor = v => (v?.includes("BUY") || v?.includes("BULL")) ? "#4ade80" : (v?.includes("SELL") || v?.includes("BEAR")) ? "#f87171" : "#a78bfa";
        const verdictBorder = v => (v?.includes("BUY") || v?.includes("BULL")) ? "rgba(74,222,128,0.3)" : (v?.includes("SELL") || v?.includes("BEAR")) ? "rgba(248,113,113,0.3)" : "rgba(167,139,250,0.3)";

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* ── Header: TEA ring + quick scores ── */}
            <div className="card" style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.07), rgba(96,165,250,0.04))", borderColor: "rgba(167,139,250,0.2)", padding: "20px 22px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                {/* TEA ring */}
                <div style={{ position: "relative", width: 90, height: 90, flexShrink: 0 }}>
                  <svg width="90" height="90" viewBox="0 0 90 90">
                    <circle cx="45" cy="45" r="36" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
                    <circle cx="45" cy="45" r="36" fill="none" stroke={teaColor} strokeWidth="7"
                      strokeDasharray={circumference} strokeDashoffset={dashOffset}
                      strokeLinecap="round" transform="rotate(-90 45 45)"
                      style={{ transition: "stroke-dashoffset 0.8s ease" }} />
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ fontSize: teaGraded ? 20 : 16, fontWeight: 900, color: teaColor, lineHeight: 1 }}>{teaGraded ? teaScore : "—"}</div>
                    <div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, letterSpacing: 0.5, marginTop: 2 }}>{teaGraded ? "TEA" : "NO GRADE"}</div>
                  </div>
                </div>

                {/* Sentiment bars */}
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", marginBottom: 12 }}>
                    ✦ Omega Intelligence · {symbol}
                  </div>
                  {[
                    // The three tea-derived bars read null when the catalyst
                    // wasn't graded; 0 would show as a hard bearish reading.
                    { label: "News Sentiment", value: tea?.sentimentScores?.news ?? teaScore, color: "#60a5fa" },
                    { label: "Earnings Alpha",  value: tea?.sentimentScores?.earnings ?? (teaGraded ? Math.round(teaScore * 0.9) : null), color: "#a78bfa" },
                    { label: "Market Regime",   value: tea?.sentimentScores?.regime ?? (regime?.regime === "bull" ? 75 : regime?.regime === "bear" ? 30 : 55), color: "#fbbf24" },
                    { label: "Options Flow",    value: tea?.sentimentScores?.options ?? (teaGraded ? Math.round(teaScore * 1.05) : null), color: "#4ade80" },
                  ].map(bar => (
                    <div key={bar.label} style={{ marginBottom: 7 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                        <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>{bar.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 800, color: bar.value == null ? "var(--muted)" : bar.color }}>{bar.value == null ? "—" : Math.min(100, Math.round(bar.value))}</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: bar.value == null ? "0%" : `${Math.min(100, Math.round(bar.value))}%`, borderRadius: 2, background: bar.color, transition: "width 0.8s ease" }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Overall badge */}
                {tea && (
                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6, fontWeight: 600 }}>SENTIMENT</div>
                    <div style={{ padding: "8px 16px", borderRadius: 12, background: sentimentColor(tea.sentiment) + "22", border: `1.5px solid ${sentimentColor(tea.sentiment)}55`, fontSize: 14, fontWeight: 900, color: sentimentColor(tea.sentiment), letterSpacing: 0.5 }}>
                      {(tea.sentiment || "NEUTRAL").toUpperCase()}
                    </div>
                    {tea.summary && (
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 8, maxWidth: 140, lineHeight: 1.5 }}>{tea.summary.slice(0, 80)}{tea.summary.length > 80 ? "…" : ""}</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Loading ── */}
            {isLoading && (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ width: 32, height: 32, border: "3px solid var(--border)", borderTopColor: "#a78bfa", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
                <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 600 }}>
                  {deepReadLoading ? `Omega is reading all ${symbol} news & TEA signals…` : `Zi is scoring ${symbol}…`}
                </div>
              </div>
            )}

            {/* ── Errors ── */}
            {(deepReadError || analysisError) && !isLoading && (
              <div style={{ padding: "14px 18px", borderRadius: 12, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#f87171", marginBottom: 4 }}>Analysis Error</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{deepReadError || analysisError}</div>
              </div>
            )}

            {/* ── Deep Read: Verdict banner ── */}
            {deepRead && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12,  }}>

                {/* Verdict + impact score */}
                <div className="card" style={{ background: `linear-gradient(135deg, ${verdictColor(deepRead.verdict)}12, transparent)`, borderColor: verdictBorder(deepRead.verdict), padding: "18px 20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: verdictColor(deepRead.verdict), letterSpacing: 0.5 }}>
                      {deepRead.verdict || "ANALYZING"}
                    </div>
                    {deepRead.impact_score != null && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>IMPACT SCORE</div>
                        <div style={{ padding: "4px 10px", borderRadius: 8, background: "rgba(255,255,255,0.06)", fontSize: 14, fontWeight: 900, color: deepRead.impact_score >= 7 ? "#f87171" : deepRead.impact_score >= 4 ? "#fbbf24" : "#4ade80" }}>
                          {deepRead.impact_score}/10
                        </div>
                      </div>
                    )}
                  </div>
                  {deepRead.summary && (
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.75 }}>{deepRead.summary}</div>
                  )}
                </div>

                {/* Price impact + Trading implication */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {deepRead.price_impact && (
                    <div className="card" style={{ padding: "16px 18px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Price Impact</div>
                      <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65 }}>{deepRead.price_impact}</div>
                    </div>
                  )}
                  {deepRead.trading_implication && (
                    <div className="card" style={{ padding: "16px 18px", background: "rgba(96,165,250,0.04)", borderColor: "rgba(96,165,250,0.2)" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Trading Implication</div>
                      <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65 }}>{deepRead.trading_implication}</div>
                    </div>
                  )}
                </div>

                {/* Symbol-specific impact */}
                {deepRead.symbol_specific_impact && (
                  <div className="card" style={{ padding: "16px 18px", background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(96,165,250,0.03))", borderColor: "rgba(167,139,250,0.2)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                      <span>✦</span> {symbol} Specific Analysis
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.75 }}>{deepRead.symbol_specific_impact}</div>
                  </div>
                )}

                {/* Catalysts + Risks */}
                {((deepRead.key_catalysts?.length) || (deepRead.risks?.length)) && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {deepRead.key_catalysts?.length > 0 && (
                      <div className="card" style={{ padding: "16px 18px", background: "rgba(74,222,128,0.04)", borderColor: "rgba(74,222,128,0.2)" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#4ade80", marginBottom: 10 }}>⬆ Key Catalysts</div>
                        {deepRead.key_catalysts.map((c, i) => (
                          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
                            <span style={{ color: "#4ade80", fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>●</span>
                            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>{c}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {deepRead.risks?.length > 0 && (
                      <div className="card" style={{ padding: "16px 18px", background: "rgba(248,113,113,0.04)", borderColor: "rgba(248,113,113,0.2)" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#f87171", marginBottom: 10 }}>⬇ Key Risks</div>
                        {deepRead.risks.map((r, i) => (
                          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
                            <span style={{ color: "#f87171", fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>●</span>
                            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>{r}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Per-headline breakdown */}
                {deepRead.headline_sentiments?.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "var(--muted)", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      News Intelligence · {deepRead.headline_sentiments.length} Articles Read
                    </div>
                    {deepRead.headline_sentiments.map((hs, i) => {
                      const sc = sentimentColor(hs.sentiment);
                      const lvl = hs.impact_level || hs.impact;
                      return (
                        <div key={i} className="card" style={{ padding: "12px 14px", background: impactBg(lvl), borderColor: impactBorder(lvl) }}>
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <div style={{ width: 3, flexShrink: 0, minHeight: 36, borderRadius: 2, background: sc, alignSelf: "stretch" }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.4, marginBottom: 5 }}>{hs.title || `Article ${i + 1}`}</div>
                              {hs.reason && (
                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.5, marginBottom: 5 }}>
                                  <span style={{ color: "#a78bfa", fontWeight: 700 }}>✦ Omega: </span>{hs.reason}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: sc, padding: "2px 7px", borderRadius: 5, background: sc + "22" }}>
                                  {(hs.sentiment || "neutral").toUpperCase()}
                                </span>
                                {lvl && (
                                  <span style={{ fontSize: 10, color: impactColor(lvl), fontWeight: 600 }}>
                                    {lvl} impact
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Zi Score Analysis (from omega/v2/analyze-symbol) ── */}
            {analysis && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10,  }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>
                  Zi Quantitative Analysis
                </div>
                {analysis.verdict && (
                  <div className="card" style={{ background: `linear-gradient(135deg, ${verdictColor(analysis.verdict)}08, transparent)`, borderColor: verdictBorder(analysis.verdict), padding: "16px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: analysis.reasoning ? 10 : 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: verdictColor(analysis.verdict) }}>{analysis.verdict}</div>
                      {analysis.zi_score != null && (
                        <div style={{ padding: "3px 10px", borderRadius: 8, background: "rgba(167,139,250,0.15)", fontSize: 12, fontWeight: 800, color: "#a78bfa" }}>
                          Zi Score {fmt(analysis.zi_score, 0)}
                        </div>
                      )}
                    </div>
                    {analysis.reasoning && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>{analysis.reasoning}</div>}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
                  {[
                    { l: "Prob. of Profit", v: analysis.probability_of_profit != null ? pct(analysis.probability_of_profit * 100) : "—" },
                    { l: "Expected Value",  v: analysis.expected_value != null ? fmt(analysis.expected_value, 3) : "—" },
                    { l: "Risk/Reward",     v: analysis.risk_reward != null ? `${fmt(analysis.risk_reward, 2)}×` : "—" },
                    { l: "Annual Vol",      v: analysis.annual_volatility != null ? pct(analysis.annual_volatility * 100) : annualVol ? fmt(annualVol, 1) + "%" : "—" },
                    { l: "Max Risk %",      v: analysis.max_risk_pct != null ? pct(analysis.max_risk_pct * 100) : "—" },
                  ].map(s => <Stat key={s.l} label={s.l} value={s.v} />)}
                </div>
              </div>
            )}

            {/* ── Refresh button ── */}
            {!isLoading && (
              <div style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
                <button
                  onClick={() => {
                    deepReadCalledRef.current = false;
                    analysisCalledRef.current = false;
                    setDeepRead(null); setAnalysis(null);
                    setDeepReadError(null); setAnalysisError(null);
                    loadDeepRead(); loadAnalysis();
                  }}
                  style={{ padding: "10px 22px", borderRadius: 12, background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", color: "#a78bfa", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  ↺ Refresh Analysis
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {quickTrade && (
        <QuickTradeModal
          position={quickTrade.position}
          mode={quickTrade.mode}
          onClose={() => setQuickTrade(null)}
          onSuccess={() => setQuickTrade(null)}
        />
      )}
    </div>
  );
}
